import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFileUploader, useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import {
    ChatInterface,
    ClaudeServiceProvider,
    FileUploaderProvider,
    hydrateChatSession,
    isDynamicFieldsComplete,
    useBriefChatMutation,
    useBriefChatSessionQuery,
} from '@99designs/feature-chat-brief';
import {
    MultiStepTopBar,
    useBriefQuery,
    useCustomerIdentifiedMutation,
} from '@99designs/feature-design-services-brief';
import { getApolloErrorType } from '@99designs/graph-utils';
import { DS_BRIEF_MULTI_STEP_URL, __url, getCurrentCulture, getSdpUrl } from '@99designs/i18n';
import { sendNavigationEvent, usePage, useSendPageViewOnce } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage, PermissionErrorPage } from '../Error';
import type { PartialDesignBrief } from '@99designs/feature-chat-brief';

export function ChatBrief() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const briefId = jobId as string;
    const locale = useMemo(() => getCurrentCulture(), []);
    const { pageData } = usePage();
    const { SignIn, isSignedIn, authorizationHeader } = useIdentityContext();
    const [briefChat] = useBriefChatMutation();
    const {
        uploadFiles,
        deleteFile,
        loading: uploaderLoading,
        error: uploaderError,
    } = useFileUploader(authorizationHeader);

    useSendPageViewOnce(locale);

    const { data, loading, error } = useBriefQuery({
        variables: { id: briefId },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    const [customerIdentified] = useCustomerIdentifiedMutation();
    const correlationId = data?.brief?.correlationId;
    const isClaimed = data?.brief?.request?.isClaimed;
    const [claimComplete, setClaimComplete] = useState(false);

    useEffect(() => {
        if (!isSignedIn || !correlationId) return;
        if (isClaimed === false) {
            customerIdentified({ variables: { requestId: correlationId } }).then(() => {
                setClaimComplete(true);
            });
        } else {
            setClaimComplete(true);
        }
    }, [isSignedIn, isClaimed, correlationId, customerIdentified]);

    const canLoadSession = isSignedIn ? claimComplete : true;

    const {
        data: chatSessionData,
        loading: chatSessionLoading,
        error: chatSessionError,
    } = useBriefChatSessionQuery({
        variables: { id: briefId },
        errorPolicy: 'all',
        skip: !canLoadSession,
    });

    const initialMessages = useMemo(
        () => hydrateChatSession(chatSessionData?.messages.messages ?? []),
        [chatSessionData]
    );

    const [designBrief, setDesignBrief] = useState<PartialDesignBrief>(() => ({
        briefId,
        createdAt: new Date(),
        updatedAt: new Date(),
    }));

    const chatTransport = useCallback(
        async (transportBriefId: string, message: string, expectedMessageCount: number) => {
            const { data, errors } = await briefChat({
                variables: { id: transportBriefId, message, expectedMessageCount },
                errorPolicy: 'all',
            });

            if (!data?.briefChat) {
                const firstError = errors?.[0];
                const serverMessage = firstError?.message;
                const errorType = firstError?.extensions?.type as string | undefined;
                if (errorType === 'INVALID_INPUT' && serverMessage) {
                    throw new Error(`invalid_argument: ${serverMessage}`);
                }
                throw new Error(serverMessage ?? 'No response from chat service');
            }

            return {
                content: data.briefChat.message.content,
                component: data.briefChat.component ?? null,
                document: data.briefChat.document ?? null,
            };
        },
        [briefChat]
    );

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        const errorType = getApolloErrorType(error);
        if (errorType === 'UNAUTHENTICATED') {
            SignIn();
            return <FullPageSpinner />;
        }
        if (errorType === 'PERMISSION_DENIED') {
            return <PermissionErrorPage />;
        }
        if (errorType === 'NOT_FOUND') {
            return <NotFoundErrorPage />;
        }
        return <ErrorPage error={error} metadata={{ briefId }} />;
    }

    if (chatSessionLoading || !canLoadSession) {
        return <FullPageSpinner />;
    }

    if (chatSessionError && getApolloErrorType(chatSessionError) === 'PERMISSION_DENIED') {
        return <PermissionErrorPage />;
    }

    if (!data) {
        return <FullPageSpinner />;
    }

    const mpvId =
        data.brief.request?.expertService?.printProduct?.mpvId ||
        data.brief.product?.fulfilmentMpvId;
    const topBarTitle =
        data.brief.request?.expertService?.title || data.brief.product?.title || 'Custom Design';
    const topBarExitUrl = mpvId ? getSdpUrl(mpvId) : __url('/design');

    const initialBriefComplete = isDynamicFieldsComplete(
        data.brief.dynamicFields as Parameters<typeof isDynamicFieldsComplete>[0]
    );

    const printMpvId = data.brief.request?.expertService?.printProduct?.mpvId;

    const serviceProductKey =
        data.brief.request?.expertService?.serviceProduct?.productKey ?? undefined;
    const serviceProductVersion =
        data.brief.request?.expertService?.serviceProduct?.pimMerchantVersion ?? undefined;
    const serviceProductOptions = data.brief.request?.facts?.serviceProductOptions ?? [];

    const handleCtaLinkClick = (url: string, label: string) => {
        sendNavigationEvent({
            ...pageData,
            category: pageData.pageSection,
            label: `Content: ${label}`,
            navigationDetail: 'CtaLinkClick',
            eventDetail: `${window.location.pathname};${url};${label};CtaLinkButton`,
        });
    };

    const handleSwitchToForm = () => {
        sendNavigationEvent({
            ...pageData,
            category: pageData.pageSection,
            label: 'Content: Fill Out Form Instead',
            navigationDetail: 'FillOutFormInstead',
            eventDetail: `${window.location.pathname};/experts/request/brief/create;Fill Out Form Instead;FillOutFormInsteadButton`,
        });
        const params = new URLSearchParams(location.state?.createSearchParams ?? '');
        params.set('chatBrief', 'false');
        const path = __url(`${DS_BRIEF_MULTI_STEP_URL}/create`);
        navigate(`${path}?${params.toString()}`);
    };

    return (
        <>
            <Helmet>
                <title>Design Services Chat Brief | Vistaprint</title>
            </Helmet>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100dvh',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: 'var(--swan-sem-color-bg-info)',
                }}
            >
                <MultiStepTopBar title={topBarTitle} exitUrl={topBarExitUrl} />
                <div style={{ flex: 1, minHeight: 0 }}>
                    <ClaudeServiceProvider sendMessage={chatTransport}>
                        <FileUploaderProvider
                            uploader={{
                                uploadFiles,
                                deleteFile,
                                loading: uploaderLoading,
                                error: uploaderError,
                            }}
                        >
                            <ChatInterface
                                designBrief={designBrief}
                                onBriefUpdate={setDesignBrief}
                                initialMessages={initialMessages}
                                initialBriefComplete={initialBriefComplete}
                                initialMaxMessages={chatSessionData?.messages.maxMessages ?? null}
                                onReview={() =>
                                    window.location.assign(
                                        `/experts/request/brief/${briefId}/review`
                                    )
                                }
                                onCtaLinkClick={handleCtaLinkClick}
                                onSwitchToForm={
                                    location.state?.createSearchParams
                                        ? handleSwitchToForm
                                        : undefined
                                }
                                printProductMpvId={printMpvId || ''}
                                requestId={correlationId}
                                serviceProductKey={serviceProductKey}
                                serviceProductVersion={serviceProductVersion}
                                serviceProductOptions={serviceProductOptions}
                            />
                        </FileUploaderProvider>
                    </ClaudeServiceProvider>
                </div>
            </div>
        </>
    );
}
